import { useEffect } from 'react';

interface DocumentMetaOptions {
  title: string;
  description?: string;
  canonical?: string;
  ogTitle?: string;
  ogDescription?: string;
  ogType?: string;
  // When true, this call is a no-op -- for a page whose title/description is built from
  // useJournalSettings() data, pass journalSettings.settingsLoading here so the tab title
  // isn't briefly overwritten with the "Your Journal Name" neutral placeholder while that
  // fetch is still in flight. Whatever title was already on the page (index.html's
  // build-time-correct default, or the previous page's title on client-side nav) is left
  // alone until real data is ready, then this hook fires once with the correct value.
  skip?: boolean;
}

export function setMetaTag(attr: 'name' | 'property', key: string, content: string) {
  let el = document.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

/** Points the page's single canonical tag at `href`, updating the prerendered tag in place. */
export function setCanonical(href: string) {
  const [first, ...extra] = Array.from(document.querySelectorAll<HTMLLinkElement>('link[rel="canonical"]'));
  extra.forEach((el) => el.remove());
  let el = first;
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', 'canonical');
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}

/** For pages that must not name a canonical address (a missing page). */
export function removeCanonical() {
  document.querySelectorAll('link[rel="canonical"]').forEach((el) => el.remove());
}

/** Sets the robots meta tag ("noindex" for sign-in pages and missing pages). */
export function setRobots(content: string) {
  setMetaTag('name', 'robots', content);
}

// react-helmet-async isn't committing head updates in this app (title/meta stay on the
// generic index.html defaults in both dev and a production build, with no console errors --
// root cause wasn't isolated). This hook sets document.title and the handful of tags each
// page needs directly, sidestepping the library rather than guessing at its internals further.
export function useDocumentMeta({ title, description, canonical, ogTitle, ogDescription, ogType, skip }: DocumentMetaOptions) {
  useEffect(() => {
    if (skip) return;
    document.title = title;
    if (description) setMetaTag('name', 'description', description);
    if (canonical) setCanonical(canonical);
    if (ogTitle) setMetaTag('property', 'og:title', ogTitle);
    if (ogDescription) setMetaTag('property', 'og:description', ogDescription);
    if (ogType) setMetaTag('property', 'og:type', ogType);
  }, [title, description, canonical, ogTitle, ogDescription, ogType, skip]);
}
